// src/types/template-studio-api.ts
// GET /api/TemplateStudio/encounter-templates → FHIR R4 Questionnaire[] (contract PROD-1155).
// PM-API BFF (proxies QuestionBank). Bearer; org from JWT (never a param). No-match → 200 [].
// Root extensions per resource: template type (valueCode A|B|C) + cps-rule (valueString).
// Does NOT carry legacy itemId/templateCode; answerOption carries valueCoding{code,display}.
//
// PROD-1494: the item shape also carries the DYNAMIC-TEMPLATE mechanisms (enableWhen /
// calculatedExpression / variable / readOnly / option ordinalValue) — all OPTIONAL. QB drops
// them today (PROD-804 persistence pending); the types + mapper are forward-wired so templates
// light up the moment the BE serves them. Extension-borne variants (SDC calculatedExpression,
// FHIR `variable`, option `ordinalValue`) are equally supported — see fhir-to-ds-questionnaire.

export interface FhirCoding {
  system?: string | null;
  code?: string | null;
  display?: string | null;
}

/** FHIR Expression datatype subset (`valueExpression` of variable/calculatedExpression). */
export interface FhirExpression {
  name?: string | null;
  language?: string | null; // 'text/fhirpath'
  expression?: string | null;
}

export interface FhirExtension {
  url: string;
  valueCode?: string | null;   // template type: 'A' | 'B' | 'C'
  valueString?: string | null; // cps-rule
  valueDecimal?: number | null;      // option ordinalValue
  valueInteger?: number | null;      // option ordinalValue (integer-typed variant)
  valueExpression?: FhirExpression | null; // calculatedExpression / variable
  /** `questionnaire-itemControl` — presentation hint (coding.code, e.g. 'symptom-chip'). */
  valueCodeableConcept?: { coding?: FhirCoding[] | null; text?: string | null } | null;
  /**
   * `questionnaire-unit` — the UCUM unit of a numeric item, as a Coding.
   *
   * This member was MISSING, which is why PA rendered every quantity question as a
   * bare number box: the wire shape the unit arrives in was not representable, so
   * nothing could read it. Same gap, now closed in its fourth repo — QuestionBank
   * always had it, the PM-API BFF was fixed for this extension, PM's Template
   * Studio was fixed in PROD-1958, and PA was the last hop still dropping it.
   *
   * A missing unit on a lab or vital is not cosmetic: mg/dL vs mmol/L on a glucose,
   * or mmHg vs kPa on a pressure, are order-of-magnitude differences a clinician
   * would otherwise have to infer from the field label.
   */
  valueCoding?: FhirCoding | null;
}

export interface FhirAnswerOption {
  valueCoding?: FhirCoding | null;
  /** Ordinal weight — either first-class (post-804) or via the base FHIR `ordinalValue` extension. */
  ordinalValue?: number | null;
  extension?: FhirExtension[] | null;
}

/** FHIR Questionnaire.item.enableWhen (R4) — the operand is one of the answer[x] variants. */
export interface FhirEnableWhen {
  question: string;
  operator: string; // 'exists' | '=' | '!=' | '>' | '<' | '>=' | '<='
  answerBoolean?: boolean | null;
  answerString?: string | null;
  answerInteger?: number | null;
  answerDecimal?: number | null;
  answerCoding?: FhirCoding | null;
}

/** One FHIR `Questionnaire.item.initial` entry — only `valueBoolean` is modelled (see `initial`). */
export interface FhirInitialValue {
  valueBoolean?: boolean | null;
}

export interface FhirQuestionnaireItem {
  linkId: string;
  text?: string | null;
  type: string;                // FHIR item.type, e.g. 'choice' | 'group'
  repeats?: boolean | null;
  /** Authored MANDATORY flag (FHIR first-class `item.required`). Read by the encounter
   *  adapter into `QItem.required`, which the DS renderer draws as a trailing ` *` on the
   *  label. Absent/null = not required — never defaulted (PROD-2039). */
  required?: boolean | null;
  readOnly?: boolean | null;
  answerOption?: FhirAnswerOption[] | null;
  enableWhen?: FhirEnableWhen[] | null;
  enableBehavior?: string | null; // 'all' | 'any' (FHIR default: all)
  /** Computed field — first-class carrier (post-804 `ExpressionJson`); the SDC
   *  `sdc-questionnaire-calculatedExpression` item extension is also read. */
  calculatedExpression?: string | null;
  /** Terminology bindings (FHIR first-class `item.code`). The rail abbreviation rides here,
   *  system `cps-abbreviation` — measured 2026-09-14: QuestionBank DROPS a `cps-abbreviation`
   *  item EXTENSION on a group and persists `code[]` verbatim in the same request (PROD-2351). */
  code?: FhirCoding[] | null;
  /** FHIR `item.initial` — the value an item starts at. Only `valueBoolean` is read: the DS
   *  `QInitialValue` carries nothing else, and a Risk `switch` (e.g. `risk-fall-present`) is the
   *  one consumer (PROD-2577). Absent/null = no authored initial. */
  initial?: FhirInitialValue[] | null;
  extension?: FhirExtension[] | null; // per-item extensions
  item?: FhirQuestionnaireItem[] | null; // nested groups (sections = sub-groups)
}

export interface FhirQuestionnaire {
  resourceType: 'Questionnaire';
  id?: string | null;
  title?: string | null;
  status?: string | null;
  extension?: FhirExtension[] | null; // root: template-type + cps-rule
  item?: FhirQuestionnaireItem[] | null;
}

export type EncounterTemplatesResponse = FhirQuestionnaire[];

/** Root-extension URLs (exact full URLs confirmed against a live response later). */
export const TEMPLATE_TYPE_EXTENSION_URL = 'template-type';
export const CPS_RULE_EXTENSION_URL = 'cps-rule';
/**
 * `cps-layout` — the author's LAYOUT document for the template (valueString, JSON). QuestionBank
 * persists only six known extension URLs and drops/400s anything else, so author-controlled
 * presentation rides THIS existing carrier rather than a new URL. PA reads one root key from it
 * today: `stepTitles` (see encounter/lib/cps-layout.ts).
 */
export const CPS_LAYOUT_EXTENSION_URL = 'cps-layout';
/**
 * `cps-scope` — the template's authored selection scope (valueString, JSON: specialtyIds,
 * practitionerIds, gender, ageFrom, ageTo, default and — TS-4 — visitTypes).
 */
export const CPS_SCOPE_EXTENSION_URL = 'cps-scope';
/**
 * `cps-selection-rank` (TS-4, PROD-2820) — RESPONSE-ONLY, on encounter-templates results:
 * `valueInteger = practitionerMatch*16 + specialtyMatch*8 + orgOwned*4 + visitTypeFit (0..2)`. Full URL
 * `https://fhir.scrips.com/StructureDefinition/cps-selection-rank`. A missing or non-integer value means
 * "unranked".
 */
export const CPS_SELECTION_RANK_EXTENSION_URL = 'cps-selection-rank';

/** Item/option extension URLs for the dynamic-template mechanisms (PROD-1494). */
export const SDC_CALCULATED_EXPRESSION_URL =
  'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-calculatedExpression';
export const FHIR_VARIABLE_URL = 'http://hl7.org/fhir/StructureDefinition/variable';
export const ORDINAL_VALUE_URL = 'http://hl7.org/fhir/StructureDefinition/ordinalValue';
/** Item presentation hint (DS-033 dispatch: 'symptom-chip' | 'slider' | 'drawing' | 'photo'). */
export const ITEM_CONTROL_URL = 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl';

/** The SDC unit extension — a UCUM `valueCoding` on a numeric item.
 *  Authored by PM's Template Studio (PROD-1958); QuestionBank requires it on every
 *  quantity item and rejects a save without it. */
export const QUESTIONNAIRE_UNIT_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-unit';
